import React, { useState, useEffect } from 'react'
import Layout from '../../brands/components/Layout'
import DataGrid from 'react-data-grid'
import './index.css'
import { Icon } from '@iconify/react'
import SelectField from '../../../components/Form/Select'
import CustomPagination from '../../../components/Pagination'
import { assignedTimeOff } from '../../dashboard/api'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { SHOW_LOADER, HIDE_LOADER } from '../../../redux/slices/LoaderSlice'
import { PersonalTimeOff } from '../../dashboard/types'
import { formatDate } from '../../resources/empresources/utils'
import { useNavigate } from 'react-router-dom'
import { getLeaveTypes } from '../../leavepolicy/leavetypes/api'
import { LeavePolicyDetail, lookupValuesI } from './type'
import { PaginatedDataLocationLookup } from '../../../features/userslisting/type'
import {
    getLookupValues,
    getLocations,
} from '../../userslisting/createuser/api'
import { getOptions } from '../../userslisting/viewuser/utils'
import { Filter } from '../../dashboard/types'

const TimeRequests: React.FC = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { leavePolicyId } = useAppSelector(
        (state) => state.persistedReducers.user
    )

    const [assignedtimeOff, setAssignedTimeOff] = useState<PersonalTimeOff[]>(
        []
    )
    const [lookupValues, setLookupValues] = useState<lookupValuesI[]>([])
    const [currentassignedPage, setCurrentAssignedPage] = useState<number>(1)
    const [assignedtotalCount, setAssignedTotalCount] = useState<number>(0)
    const assignedtotalPages = Math.ceil(assignedtotalCount / 10)
    const [leaveTypeOptions, setLeaveTypeOptions] = useState<
        { value: string | number; label: string }[]
    >([])
    const [locationsLookupValues, setLocationsLookupValues] =
        useState<PaginatedDataLocationLookup | null>(null)
    const [selectedLocation, setSelectedLocation] = useState<string | null>(
        null
    )
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null)
    const [selectedLeaveType, setSelectedLeaveType] = useState<
        string | number | null
    >(null)

    useEffect(() => {
        dispatch(SHOW_LOADER())
        const getLookUpData = async () => {
            try {
                const lookupValues = await getLookupValues()
                const locations = await getLocations()
                setLookupValues(lookupValues.data)
                setLocationsLookupValues(locations.data)
            } catch (err) {
                console.error('Error fetching:', err)
            } finally {
                dispatch(HIDE_LOADER())
            }
        }
        getLookUpData()
    }, [])

    useEffect(() => {
        const getassignedtimeOff = async () => {
            dispatch(SHOW_LOADER())
            try {
                const filters: Filter[] = []

                if (selectedLocation !== '') {
                    filters.push({
                        field: 'LocationId',
                        operator: 'eq',
                        value: selectedLocation,
                    })
                }

                if (selectedStatus !== '') {
                    filters.push({
                        field: 'StatusId',
                        operator: 'eq',
                        value: Number(selectedStatus),
                    })
                }

                if (selectedLeaveType !== '') {
                    filters.push({
                        field: 'LeaveTypeId',
                        operator: 'eq',
                        value: selectedLeaveType,
                    })
                }

                const filteredFilters = filters.filter(
                    (filter) =>
                        filter.value !== null &&
                        filter.value !== '' &&
                        filter.value !== 0
                )
                const logic = filteredFilters?.length === 0 ? '' : 'and'
                if (filteredFilters?.length > 0) {
                    setCurrentAssignedPage(1)
                }
                const response = await assignedTimeOff(
                    10,
                    currentassignedPage,
                    filteredFilters,
                    logic
                )
                response?.data?.data?.sort(
                    (a: { requestedOn: string }, b: { requestedOn: string }) =>
                        new Date(b.requestedOn).getTime() -
                        new Date(a.requestedOn).getTime()
                )
                setAssignedTimeOff(response?.data?.data)
                setAssignedTotalCount(response?.data?.totalCount)
            } catch (err) {
                console.error('Error fetching:', err)
            } finally {
                dispatch(HIDE_LOADER())
            }
        }
        getassignedtimeOff()
    }, [
        currentassignedPage,
        selectedLocation,
        selectedStatus,
        selectedLeaveType,
    ])

    useEffect(() => {
        const fetchData = async () => {
            if (leavePolicyId) {
                try {
                    dispatch(SHOW_LOADER())
                    const response = await getLeaveTypes({})

                    const leavePolicyDetails = response?.data?.data

                    const validLeaveTypes = leavePolicyDetails.map(
                        (policy: LeavePolicyDetail) => ({
                            value: policy.id,
                            label: policy.title,
                        })
                    )
                    setLeaveTypeOptions(validLeaveTypes)
                } catch (error) {
                    console.error(error)
                } finally {
                    dispatch(HIDE_LOADER())
                }
            }
        }
        fetchData()
    }, [leavePolicyId])

    const locationOptions =
        locationsLookupValues?.data.map((location) => ({
            value: location.id,
            label: location.name,
        })) || []

    const getCurrentPage = (page: number) => {
        setCurrentAssignedPage(page)
    }

    const statusOptions = getOptions(lookupValues, 'Status').filter(
        (item) => item.label !== 'Assigned' && item.label !== 'Completed'
    )

    const column = [
        {
            key: 'name',
            name: `Name`,
            resizable: true,
            sortable: true,
        },
        {
            key: 'location',
            name: 'Location',
            resizable: true,
            sortable: true,
        },
        {
            key: 'leaveType',
            name: `Type`,
            resizable: true,
            sortable: true,
        },
        {
            key: 'status',
            name: `Status`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return (
                    <div
                        className={
                            props.row.status === 'Pending'
                                ? 'status'
                                : props.row.status === 'Approved'
                                  ? 'appr'
                                  : 'rej'
                        }
                    >
                        {props.row.status}
                    </div>
                )
            },
        },
        {
            key: 'date',
            name: `Dates`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return (
                    <div className="">
                        {formatDate(props.row.startDate)} -{' '}
                        {formatDate(props.row.endDate)}
                    </div>
                )
            },
        },
        {
            key: 'action',
            name: `Actions`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return (
                    <div className="dropdown-custom-link">
                        <Icon
                            icon="mdi:eye"
                            className="icon-success"
                            cursor={'pointer'}
                            onClick={() =>
                                navigate(`/time-off/view/${props.row.id}`)
                            }
                        />
                    </div>
                )
            },
        },
    ]

    return (
        <div>
            <Layout>
                <div className="col-12">
                    <div className="page-information mb-3">
                        <div className="row ">
                            <div className="col-sm-3">
                                <h4 className="mt-3">Time-Off Requests</h4>
                            </div>
                            <div className="col-sm-3">
                                <div className="row">
                                    <div className="col-12">
                                        <SelectField
                                            name="exampleSelect"
                                            label=""
                                            options={[
                                                {
                                                    value: '',
                                                    label: 'Location:',
                                                },
                                                ...locationOptions,
                                            ]}
                                            value={selectedLocation || ''}
                                            onChange={(e) =>
                                                setSelectedLocation(
                                                    e.target.value
                                                )
                                            }
                                            onBlur={() => {
                                                return
                                            }}
                                            error={''}
                                            touched={true}
                                            defaultValue="option1"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-3">
                                <div className="row">
                                    <div className="col-12">
                                        <SelectField
                                            name="leaveType"
                                            label=""
                                            options={[
                                                {
                                                    value: '',
                                                    label: 'Leave Type:',
                                                },
                                                ...leaveTypeOptions,
                                            ]}
                                            value={selectedLeaveType as string}
                                            onChange={(e) =>
                                                setSelectedLeaveType(
                                                    e.target.value
                                                )
                                            }
                                            onBlur={() => {
                                                return
                                            }}
                                            error={''}
                                            touched={true}
                                            defaultValue="option1"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-3">
                                <div className="row">
                                    <div className="col-12 custom-drpdwn">
                                        <SelectField
                                            name="status"
                                            label=""
                                            options={[
                                                {
                                                    value: 0,
                                                    label: 'Status:',
                                                },
                                                ...statusOptions,
                                            ]}
                                            value={selectedStatus as string}
                                            onChange={(e) =>
                                                setSelectedStatus(
                                                    e.target.value
                                                )
                                            }
                                            onBlur={() => {
                                                return
                                            }}
                                            error={''}
                                            touched={true}
                                            defaultValue="option1"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <>
                        {assignedtimeOff && assignedtimeOff?.length > 0 ? (
                            <DataGrid
                                className="data-grid-table"
                                columns={column}
                                rows={assignedtimeOff}
                                rowHeight={60}
                                headerRowHeight={40}
                            />
                        ) : (
                            <>
                                <div className="no-records">
                                    There are no Time-Off Requests.
                                </div>
                            </>
                        )}
                        <div className="mt-3">
                            {assignedtimeOff?.length > 0 && (
                                <CustomPagination
                                    totalPages={assignedtotalPages}
                                    currentPage={currentassignedPage}
                                    getPageNumber={(page: number) =>
                                        getCurrentPage(page)
                                    }
                                />
                            )}{' '}
                        </div>
                    </>
                </div>
            </Layout>
        </div>
    )
}

export default TimeRequests
